import React from 'react';
import { Message } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { useEntryHistory } from '../../hooks/useTimesheet';
import { formatDate, formatTimestamp } from '../../utils/timesheet';

/**
 * What changed on time entries, who changed it and when - from the history
 * the database keeps of every create, change and delete
 * (timesheet_entry_history). One list, used for a single entry (its modal)
 * and for a pay period (the Change History section, which includes entries
 * since deleted).
 */

const ACTIONS = {
  created: { label: 'Added', icon: 'bi-plus-circle', tone: 'good' },
  updated: { label: 'Changed', icon: 'bi-pencil', tone: 'info' },
  deleted: { label: 'Deleted', icon: 'bi-trash3', tone: 'bad' },
};

// Where an entry came from, when it was not typed in.
const SOURCES = {
  timer: 'from the timer',
  time_off: 'from a time-off request',
  timecamp_import: 'from the TimeCamp import',
};

const shown = (value) => (value === null || value === undefined || value === '' ? '—' : value);

export function HistoryList({ events, showEntry = false }) {
  if (!events?.length) {
    return <p className="text-muted mb-0">No changes recorded.</p>;
  }
  return (
    <ul className="ts-history">
      {events.map((event) => {
        const meta = ACTIONS[event.action] || ACTIONS.updated;
        const source = event.action === 'created' ? SOURCES[event.source] : null;
        return (
          <li key={event.id} className={`ts-history__item ts-history__item--${meta.tone}`}>
            <i className={`bi ${meta.icon}`} aria-hidden="true" />
            <div className="ts-history__body">
              <div className="ts-history__head">
                <strong>{meta.label}</strong>
                {event.by ? ` by ${event.by.name}` : ''}
                {source ? ` ${source}` : ''}
                <span className="ts-history__when"> · {formatTimestamp(event.at)}</span>
              </div>
              {showEntry || event.action !== 'updated' ? (
                <div className="ts-history__entry">
                  {event.entry.date ? `${formatDate(event.entry.date)} · ` : ''}
                  {event.entry.task || 'Unknown task'}
                  {event.action !== 'updated' ? ` · ${event.entry.length}` : ''}
                  {event.action !== 'updated' && event.entry.start && event.entry.end ? ` (${event.entry.start}–${event.entry.end})` : ''}
                </div>
              ) : null}
              {event.changes?.length ? (
                <ul className="ts-history__changes">
                  {event.changes.map((change) => (
                    <li key={change.field}>
                      <span className="ts-history__field">{change.label}</span>
                      <span className="ts-history__from">{shown(change.from)}</span>
                      <i className="bi bi-arrow-right" aria-hidden="true" />
                      <span className="ts-history__to">{shown(change.to)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// One entry's history, newest first.
export default function EntryHistoryModal({ entryId, onClose }) {
  // The last entry shown, so the history holds while the dialog fades out.
  const shownId = useLastPresent(entryId);
  const historyQuery = useEntryHistory(shownId, { enabled: Boolean(entryId) });
  return (
    <Modal open={Boolean(entryId)} onClose={onClose} size="sm">
      <Modal.Header><Modal.Title>Entry History</Modal.Title></Modal.Header>
      <Modal.Body>
        {historyQuery.error ? <Message type="error" showIcon>{String(historyQuery.error?.message || historyQuery.error)}</Message> : null}
        {historyQuery.isLoading ? <p className="text-muted mb-0">Loading…</p> : null}
        {historyQuery.data?.installed === false ? (
          <Message type="info" showIcon>History starts once server/sql/timesheet_system.sql has been re-run.</Message>
        ) : null}
        {historyQuery.data?.events ? <HistoryList events={historyQuery.data.events} /> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
